import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '@/app/store/hooks';
import { resetQuote } from '../store/quoteSlice';

const JOURNEY_OWNER_KEY = 'quote-journey-owner';

// sessionStorage is per-tab and survives a reload; the persisted quote slice is
// neither, so this is what separates "refresh" from "new tab" or "older entry".
const readJourneyOwner = (): string | null => {
    try {
        return sessionStorage.getItem(JOURNEY_OWNER_KEY);
    } catch {
        return null;
    }
};

const claimJourney = (key: string): void => {
    try {
        sessionStorage.setItem(JOURNEY_OWNER_KEY, key);
    } catch {
        // Storage refused. Resume is a nicety; a fresh quote is the safe fallback.
    }
};

// One journey per history entry; location.key survives a refresh, so a reload keeps its journey.
// `sameJourney` is latched per entry before the layout effect claims it.
export const useNewQuoteReset = (): { sameJourney: boolean } => {
    const { key } = useLocation();
    const dispatch = useAppDispatch();
    const latch = useRef<{ key: string; sameJourney: boolean } | null>(null);
    if (latch.current?.key !== key) {
        latch.current = { key, sameJourney: readJourneyOwner() === key };
    }

    useLayoutEffect(() => {
        if (readJourneyOwner() === key) return;

        claimJourney(key);
        dispatch(resetQuote());
    }, [key, dispatch]);

    return { sameJourney: latch.current.sameJourney };
};
